import { Observable } from 'rxjs';
import { OdataQueryOptions } from '../../core';

export class PaginationUtils {
  /**
   * Generic utility to fetch all items from a paginated OData endpoint.
   * It keeps requesting pages using $skip/$top until an empty page is returned.
   *
   * @param initialQuery The base OdataQueryOptions to start from (filters, orderBy, expand, etc.).
   * @param fetchPage A function that accepts OdataQueryOptions (with updated skip/top) and returns an Observable of a response that contains an array.
   * @param options Optional configuration
   *   - batchSize: number of items to request per page (default 100)
   *   - select: function to extract the items array from the response (default: r => r?.value ?? [])
   */
  static fetchAllPages<TResponse extends { value?: TItem[] }, TItem>(
    initialQuery: OdataQueryOptions,
    fetchPage: (q: OdataQueryOptions) => Observable<TResponse>,
    options?: {
      batchSize?: number;
      select?: (response: TResponse) => TItem[];
    },
  ): Observable<TItem[]> {
    const batchSize = options?.batchSize ?? 100;
    const select = options?.select ?? (r => (r.value ?? []) as TItem[]);

    return new Observable<TItem[]>(subscriber => {
      let currentSkip = 0;
      const all: TItem[] = [];

      const fetchNext = () => {
        const q = new OdataQueryOptions({ ...initialQuery });
        q.skip = currentSkip;
        q.top = batchSize;

        const sub = fetchPage(q).subscribe({
          next: resp => {
            const items = select(resp) ?? [];
            if (items.length > 0) {
              all.push(...items);
              currentSkip += items.length;
              fetchNext();
            } else {
              subscriber.next(all);
              subscriber.complete();
            }
          },
          error: err => subscriber.error(err),
        });

        // Ensure teardown unsubscribes inner subscription
        subscriber.add(sub);
      };

      fetchNext();
    });
  }
}
